import type { ManufacturingCase, ManufacturingVariant } from '@infinitform/platform-core';
import { computeCaseLabel } from './case-sync.util';

/** One (method, material) pair, ready to be flattened into a child config. */
export interface ExpandedVariant {
  case: ManufacturingCase;
  variant: ManufacturingVariant;
}

/** Minimal material shape this module needs. */
export interface VariantMaterialRef {
  id: string;
  name?: string;
}

/**
 * Human-readable label for one variant, e.g. "Extrusion (X+) — PETG".
 * Sole writer of `ManufacturingVariant.label`; refreshed on project load so
 * renamed materials and renamed methods never leave a stale string behind.
 */
export function computeVariantLabel(c: ManufacturingCase, materialName: string): string {
  const caseLabel = computeCaseLabel(c);
  return materialName ? `${caseLabel} — ${materialName}` : caseLabel;
}

/** A variant with no overrides — what adding a material to a method produces. */
export function makeDefaultVariant(variantId: string, materialId: string): ManufacturingVariant {
  return { variantId, materialId };
}

/**
 * The material the model is made of: the one assigned to the most design
 * parts (all parts while none is marked design), where a part without a valid
 * assignment counts as `materials[0]` — the XML writer's fallback. Ties go to
 * catalog order. Null for an empty catalog.
 */
export function resolveModelMaterialId(
  materials: readonly VariantMaterialRef[],
  partMaterials: Readonly<Record<string, string>> | undefined,
  parts: { design?: readonly string[]; nondesign?: readonly string[] } | undefined,
): string | null {
  if (materials.length === 0) return null;
  const design = parts?.design ?? [];
  const partIds = design.length > 0 ? design : (parts?.nondesign ?? []);
  const knownIds = new Set(materials.map((m) => m.id));

  const counts = new Map<string, number>();
  for (const partId of partIds) {
    const assigned = partMaterials?.[partId];
    const materialId = assigned && knownIds.has(assigned) ? assigned : materials[0].id;
    counts.set(materialId, (counts.get(materialId) ?? 0) + 1);
  }

  let best = materials[0].id;
  for (const m of materials) {
    if ((counts.get(m.id) ?? 0) > (counts.get(best) ?? 0)) best = m.id;
  }
  return best;
}

/**
 * True for a variant that is still exactly what adding the method produced —
 * no name and no overrides — so moving it to another material loses nothing
 * the user chose.
 */
export function isPristineVariant(v: ManufacturingVariant): boolean {
  return (
    !v.name?.trim() &&
    v.goalOverride === undefined &&
    (v.loadcaseOverrides === undefined || Object.keys(v.loadcaseOverrides).length === 0)
  );
}

/**
 * Expand cases into one entry per child run.
 *
 * Tolerates a missing or empty `variants` array by synthesising a single
 * variant on `materials[0]`, reusing the case's own `methodId` as the
 * variantId so an unmigrated project still links to its existing child.
 */
export function expandCaseVariants(
  cases: readonly ManufacturingCase[],
  materials: readonly VariantMaterialRef[],
): ExpandedVariant[] {
  const pairs: ExpandedVariant[] = [];
  for (const c of cases) {
    if (c.variants && c.variants.length > 0) {
      for (const variant of c.variants) pairs.push({ case: c, variant });
      continue;
    }
    const fallback = materials[0];
    if (!fallback) continue;
    pairs.push({ case: c, variant: { variantId: c.methodId, materialId: fallback.id } });
  }
  return pairs;
}

/**
 * Map every variantId to its display label, for joining children back to
 * cases. `child.methodId` holds the variantId, not a case's own methodId, so
 * any lookup from a child must be keyed the same way this map is built: a
 * case with a non-empty `variants` array registers each variant's id, and a
 * case without one registers its own `methodId` as the synthesised variantId
 * (matching `expandCaseVariants`'s unmigrated-case fallback).
 *
 * Intentional near-duplicate of `buildVariantLabelMap` in
 * `backend/src/resources/project/multi-manufacturing/expand-variants.ts` and
 * the desktop copy under
 * `apps/desktop-shell/electron/modules/queue/multi-manufacturing/expand-variants.ts`.
 * Keep all three in sync manually.
 */
export function buildVariantLabelMap(cases: readonly ManufacturingCase[]): Map<string, string> {
  const labelByVariantId = new Map<string, string>();
  for (const c of cases) {
    if (c.variants && c.variants.length > 0) {
      for (const v of c.variants) {
        labelByVariantId.set(v.variantId, v.name?.trim() || v.label || c.label || c.method);
      }
    } else {
      labelByVariantId.set(c.methodId, c.label ?? c.method);
    }
  }
  return labelByVariantId;
}
